import React, { useState } from 'react';

/**
 * Reusable AccordionList Component with Modern UI Design
 * 
 * @param {Array} data - Array of data objects to display
 * @param {Array} primaryFields - Fields to show in collapsed state [{key, label, render?}]
 * @param {Array} expandedFields - Fields to show in expanded state [{key, label, render?, gridCols?}]
 * @param {Function} getRowKey - Function to get unique key for each row (default: item => item.id)
 * @param {Function} renderActions - Optional function to render action buttons (receives item, isExpanded)
 * @param {String} emptyMessage - Message to show when no data
 * @param {Boolean} allowMultipleExpand - Allow multiple rows expanded at once (default: true)
 * @param {String} expandedGridCols - Default grid columns class for expanded section
 * @param {String} className - Additional CSS classes
 */
const AccordionList = ({
    data = [],
    primaryFields = [],
    expandedFields = [],
    getRowKey = (item) => item.id,
    renderActions,
    emptyMessage = 'No data found!',
    allowMultipleExpand = true,
    expandedGridCols = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    className = '',
}) => {
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [hoveredRow, setHoveredRow] = useState(null);

    const toggleRowExpansion = (rowKey) => {
        // Ensure rowKey is always a string for consistent Set operations
        const key = String(rowKey);
        setExpandedRows(prev => {
            // Convert all previous values to strings for consistency
            const prevAsStrings = allowMultipleExpand 
                ? new Set(Array.from(prev).map(k => String(k)))
                : new Set();
            
            if (prevAsStrings.has(key)) {
                prevAsStrings.delete(key);
            } else {
                prevAsStrings.add(key);
            }
            return prevAsStrings;
        });
    };

    const isExpanded = (rowKey) => {
        // Ensure rowKey is always a string for consistent Set operations
        const key = String(rowKey);
        return expandedRows.has(key);
    };

    const renderFieldValue = (item, field, index) => {
        if (field.render && typeof field.render === 'function') {
            return field.render(item[field.key], item, index);
        }
        return item[field.key] || '—';
    };

    if (!data || data.length === 0) {
        return (
            <div className={`bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 p-16 text-center ${className}`}>
                <div className="flex flex-col items-center justify-center">
                    <div className="relative mb-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-xl opacity-20 animate-pulse"></div>
                        <svg
                            className="relative w-20 h-20 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                    </div>
                    <p className="text-gray-600 text-lg font-semibold">{emptyMessage}</p>
                    <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {data.map((item, index) => {
                const rowKey = getRowKey(item);
                // Ensure rowKey is always a valid key (string or number)
                const validRowKey = rowKey !== null && rowKey !== undefined ? String(rowKey) : `row-${index}`;
                const expanded = isExpanded(validRowKey);
                const isHovered = hoveredRow === validRowKey;

                return (
                    <div
                        key={validRowKey}
                        className="group relative"
                        onMouseEnter={() => setHoveredRow(validRowKey)}
                        onMouseLeave={() => setHoveredRow(null)}
                    >
                        {/* Glow effect on hover */}
                        {isHovered && (
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                        )}
                        
                        <div
                            className={`relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 ${
                                expanded 
                                    ? 'shadow-xl border-blue-200 ring-2 ring-blue-50' 
                                    : 'hover:shadow-lg hover:border-gray-200'
                            } ${isHovered ? 'transform scale-[1.01]' : ''}`}
                        >
                            {/* Gradient accent bar */}
                            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-opacity duration-300 ${
                                expanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                            }`}></div>

                            {/* Primary Row - Always Visible */}
                            <div
                                className="px-6 py-5 cursor-pointer transition-all duration-300 relative overflow-hidden"
                                onClick={() => toggleRowExpansion(validRowKey)}
                            >
                                {/* Background gradient on hover */}
                                <div className={`absolute inset-0 bg-gradient-to-r from-blue-50/0 via-purple-50/0 to-pink-50/0 transition-all duration-300 ${
                                    isHovered ? 'from-blue-50/30 via-purple-50/20 to-pink-50/30' : ''
                                }`}></div>
                                
                                <div className="relative flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-6 flex-1 flex-wrap">
                                        {primaryFields.map((field, fieldIndex) => (
                                            <div
                                                key={field.key || fieldIndex}
                                                className={`${field.className || ''} ${
                                                    field.flex ? 'flex-1' : ''
                                                } min-w-0 transition-transform duration-200 ${
                                                    isHovered ? 'transform translate-x-1' : ''
                                                }`}
                                            >
                                                <div className="text-[10px] text-gray-500 uppercase font-bold mb-2 tracking-widest">
                                                    {field.label}
                                                </div>
                                                <div className={`text-sm font-semibold text-gray-900 break-words transition-colors duration-200 ${
                                                    field.highlight ? 'text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent' : ''
                                                } ${isHovered && !field.highlight ? 'text-gray-700' : ''}`}>
                                                    {renderFieldValue(item, field, index)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Actions and Expand Icon */}
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        {renderActions && (
                                            <div
                                                className="flex items-center gap-2"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {renderActions(item, expanded)}
                                            </div>
                                        )}
                                        
                                        {/* Modern Expand/Collapse Icon */}
                                        <div className="flex-shrink-0 relative">
                                            <div className={`absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-md opacity-0 transition-opacity duration-300 ${
                                                expanded ? 'opacity-30' : 'group-hover:opacity-20'
                                            }`}></div>
                                            <div className={`relative w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 transition-all duration-300 ${
                                                expanded 
                                                    ? 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 rotate-180 shadow-md' 
                                                    : 'group-hover:from-blue-50 group-hover:to-purple-50 group-hover:border-blue-200 group-hover:shadow-md'
                                            }`}>
                                                <svg
                                                    className={`w-4 h-4 text-gray-600 transition-all duration-300 ${
                                                        expanded ? 'text-blue-600' : 'group-hover:text-blue-600'
                                                    }`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2.5}
                                                        d="M19 9l-7 7-7-7"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Details with Modern Animation */}
                            {expanded && expandedFields.length > 0 && (
                                <div className="border-t border-gray-200 bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 px-6 py-6 overflow-hidden">
                                    <div className="relative">
                                        {/* Animated background pattern */}
                                        <div className="absolute inset-0 opacity-5">
                                            <div className="absolute inset-0" style={{
                                                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(99, 102, 241, 0.3) 1px, transparent 0)',
                                                backgroundSize: '24px 24px'
                                            }}></div>
                                        </div>
                                        
                                        <div className={`relative grid gap-4 ${expandedGridCols}`}>
                                            {expandedFields.map((field, fieldIndex) => {
                                                const renderedValue = renderFieldValue(item, field, index);
                                                const isEmpty = renderedValue === '—' || renderedValue === null || renderedValue === '';
                                                
                                                return (
                                                    <div
                                                        key={field.key || fieldIndex}
                                                        className={`${field.className || ''} transform transition-all duration-300 ${
                                                            isEmpty ? 'opacity-60' : 'opacity-100'
                                                        }`}
                                                        style={{
                                                            animation: `fadeInUp 0.4s ease-out ${fieldIndex * 0.03}s both`
                                                        }}
                                                    >
                                                        <div className="flex flex-col h-full bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-100 hover:border-gray-300 hover:shadow-md transition-all duration-200 hover:bg-white/90">
                                                            {/* Label */}
                                                            <div className="mb-2.5 flex-shrink-0">
                                                                <div className="text-[11px] text-gray-500 uppercase font-semibold tracking-wider leading-tight">
                                                                    {field.label}
                                                                </div>
                                                            </div>
                                                            {/* Value */}
                                                            <div className={`flex-1 text-sm text-gray-900 break-words leading-relaxed min-h-[1.5rem] ${
                                                                field.mono ? 'font-mono text-xs' : ''
                                                            } ${field.highlight ? 'font-semibold text-base' : ''} ${
                                                                isEmpty ? 'text-gray-400 italic' : ''
                                                            }`}>
                                                                {renderedValue}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AccordionList;
